import React, { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { companyInfo } from '../data'

export default function Header() {
  const [isOpen, setIsOpen] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 1200)
  const [isScrolled, setIsScrolled] = useState(false)
  const location = useLocation()
  const dropdownRef = useRef(null)

  // Track scroll position to apply scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true)
      } else {
        setIsScrolled(false)
      }
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Track window size for the 1200px burger menu breakpoint
  useEffect(() => {
    const handleResize = () => {
      const large = window.innerWidth >= 1200
      setIsLargeScreen(large)
      if (large) {
        setIsOpen(false) // Close mobile menu if screen becomes large
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Prevent body scroll when mobile sidebar is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false)
    setIsDropdownOpen(false)
  }, [location])

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  const navLinks = [
    { name: 'Forside', path: '/' },
    {
      name: 'Ydelser',
      path: '/ydelser',
      dropdown: [
        { name: 'Privat rengøring', path: '/ydelser/privat-rengoering' },
        { name: 'Housekeeping', path: '/ydelser/housekeeping' },
        { name: 'Erhvervsrengøring', path: '/ydelser/erhvervsrengoering' },
      ],
    },
    { name: 'Om os', path: '/om-os' },
    { name: 'Galleri', path: '/galleri' },
    { name: 'Priser', path: '/priser' },
    { name: 'Kontakt', path: '/kontakt' },
  ]

  return (
    <header className={`sticky top-0 z-40 w-full bg-white border-b border-brand-border transition-all duration-300 ${
      isScrolled ? 'py-2 lg:py-3 shadow-md' : 'py-3.5 lg:py-4 shadow-sm'
    }`}>
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <img
            src={companyInfo.logo}
            alt={companyInfo.name}
            className={`w-auto object-contain transition-all duration-300 ${
              isScrolled ? 'h-14 sm:h-16 lg:h-18 max-h-18' : 'h-18 sm:h-20 lg:h-24 max-h-24'
            }`}
          />
        </Link>

        {/* Desktop Navigation (>= 1200px) */}
        {isLargeScreen && (
          <nav className="flex items-center gap-8">
            {navLinks.map((link) => {
              if (link.dropdown) {
                return (
                  <div
                    key={link.name}
                    ref={dropdownRef}
                    className="relative"
                    onMouseEnter={() => setIsDropdownOpen(true)}
                    onMouseLeave={() => setIsDropdownOpen(false)}
                  >
                    <button
                      className={`flex items-center gap-1.5 py-2 text-sm font-semibold transition-colors duration-200 hover:text-black focus:outline-none ${
                        isActive(link.path) ? 'text-black' : 'text-brand-text-dark'
                      }`}
                    >
                      {link.name}
                      <i className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
                    </button>

                    {/* Dropdown Menu */}
                    {isDropdownOpen && (
                      <div className="absolute left-0 mt-1 w-56 rounded-lg border border-brand-border bg-white p-2 shadow-lg animate-in fade-in slide-in-from-top-2 duration-200">
                        {link.dropdown.map((subLink) => (
                          <Link
                            key={subLink.name}
                            to={subLink.path}
                            className={`block rounded-md px-4 py-2.5 text-sm font-medium text-brand-text-dark hover:bg-brand-gray hover:text-black transition-colors duration-150 ${
                              isActive(subLink.path) ? 'bg-brand-gray text-black font-semibold' : ''
                            }`}
                          >
                            {subLink.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                )
              }

              return (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`relative py-2 text-sm font-medium transition-colors duration-200 hover:text-black ${
                    isActive(link.path) ? 'text-black font-semibold' : 'text-brand-text-dark'
                  }`}
                >
                  {link.name}
                </Link>
              )
            })}
          </nav>
        )}

        {/* Desktop CTA (>= 1200px) */}
        {isLargeScreen && (
          <Link
            to="/kontakt"
            className="inline-flex items-center justify-center rounded-lg bg-brand-navy px-5 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-brand-darkblue hover:shadow-md focus:outline-none"
          >
            Få et tilbud
          </Link>
        )}

        {/* Mobile Burger Button (< 1200px) */}
        {!isLargeScreen && (
          <button
            onClick={() => setIsOpen(true)}
            className="inline-flex items-center justify-center w-8 h-8 rounded-md text-brand-text-dark focus:outline-none transition-transform duration-200 active:scale-95"
            aria-label="Open menu"
          >
            <i className="fa-solid fa-bars text-lg"></i>
          </button>
        )}
      </div>

      {/* Mobile Sidebar Navigation (< 1200px) */}
      {!isLargeScreen && (
        <>
          {/* Backdrop Overlay */}
          <div
            className={`fixed inset-0 z-50 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${
              isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            onClick={() => setIsOpen(false)}
          />

          {/* Sidebar Panel */}
          <div
            className={`fixed top-0 right-0 bottom-0 z-50 w-full max-w-[320px] bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-in-out ${
              isOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            {/* Sidebar Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-brand-border">
              <Link to="/" onClick={() => setIsOpen(false)} className="flex items-center">
                <img
                  src={companyInfo.logo}
                  alt={companyInfo.name}
                  className="h-16 sm:h-20 w-auto object-contain"
                />
              </Link>
              <button
                onClick={() => setIsOpen(false)}
                className="inline-flex items-center justify-center w-8 h-8 rounded-md text-brand-text-dark hover:text-black focus:outline-none transition-transform duration-200 active:scale-95"
                aria-label="Close menu"
              >
                <i className="fa-solid fa-xmark text-lg"></i>
              </button>
            </div>

            {/* Sidebar Content (Scrollable) */}
            <div className="flex-1 overflow-y-auto px-6 py-8">
              <nav className="flex flex-col gap-5">
                {navLinks.map((link) => {
                  if (link.dropdown) {
                    return (
                      <div key={link.name} className="flex flex-col">
                        <button
                          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                          className={`flex items-center justify-between py-2 text-base font-semibold transition-colors duration-200 hover:text-black focus:outline-none ${
                            isActive(link.path) ? 'text-black' : 'text-brand-text-dark'
                          }`}
                        >
                          {link.name}
                          <i className={`fa-solid fa-chevron-down text-sm transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
                        </button>

                        {/* Mobile Dropdown Links */}
                        <div className={`grid transition-all duration-300 ease-in-out ${
                          isDropdownOpen ? 'grid-rows-[1fr] opacity-100 mt-3' : 'grid-rows-[0fr] opacity-0'
                        }`}>
                          <div className="min-h-0 overflow-hidden flex flex-col gap-3.5 pl-4 border-l-2 border-brand-border py-1.5">
                            {link.dropdown.map((subLink) => (
                              <Link
                                key={subLink.name}
                                to={subLink.path}
                                onClick={() => setIsOpen(false)}
                                className={`py-2 text-sm font-medium text-brand-text-muted hover:text-black transition-colors duration-150 ${
                                  isActive(subLink.path) ? 'text-black font-semibold' : ''
                                }`}
                              >
                                {subLink.name}
                              </Link>
                            ))}
                          </div>
                        </div>
                      </div>
                    )
                  }

                  return (
                    <Link
                      key={link.name}
                      to={link.path}
                      onClick={() => setIsOpen(false)}
                      className={`py-2 text-base font-semibold transition-colors duration-200 hover:text-black ${
                        isActive(link.path) ? 'text-black font-semibold' : 'text-brand-text-dark'
                      }`}
                    >
                      {link.name}
                    </Link>
                  )
                })}
              </nav>
            </div>

            {/* Sidebar Footer */}
            <div className="p-6 border-t border-brand-border bg-brand-gray/30">
              <Link
                to="/kontakt"
                onClick={() => setIsOpen(false)}
                className="block w-full rounded-none bg-brand-navy py-3 text-center text-sm font-semibold text-white transition-all duration-200 hover:bg-brand-darkblue shadow-sm"
              >
                Få et tilbud
              </Link>
              
              {/* Contact Info in Sidebar */}
              <div className="mt-6 space-y-2 text-xs text-brand-text-muted">
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-phone text-black w-4"></i>
                  <a href={`tel:${companyInfo.phone.replace(/\s+/g, '')}`} className="hover:text-black transition-colors">{companyInfo.phone}</a>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fa-solid fa-envelope text-black w-4"></i>
                  <a href={`mailto:${companyInfo.email}`} className="hover:text-black transition-colors">{companyInfo.email}</a>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </header>
  )
}
